import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const LocaleContext = createContext();
const LOCALE_KEY = 'gl.locale';

const messages = {
  en: {
    brand_title: 'GL Compra de Ouro',
    customers: 'Customers',
    search_placeholder: 'Search by name, info, or phone',
    signed_in_as: 'Signed in as',
    logout: 'Logout',
    current_balance: 'Current Balance',
    make_transaction: 'Make a Transaction',
    type: 'Type',
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    amount: 'Amount',
    description: 'Description',
    description_placeholder: 'e.g., Deposit from gold sale',
    date: 'Date',
    add_transaction: 'Add Transaction',
    transaction_history: 'Transaction History',
    no_transactions: 'No transactions yet',
    no_customers: 'No customers found',
    select_customer: 'Select a customer to view details',
    // Customers
    add_client: 'Add Client',
    new_client: 'New Client',
    edit_client: 'Edit Client',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    additional_info: 'Additional Info',
    cancel: 'Cancel',
    create: 'Create',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    delete_confirm: 'Delete this customer? This will also remove their transactions.',
    delete_tx_confirm: 'Delete this transaction?',
    edit_transaction: 'Edit Transaction',
    saving: 'Saving…',
    invalid_amount: 'Invalid amount',
    all_clients_total: 'All Clients Balance',
    transaction_statement: 'Transaction Statement',
    generated: 'Generated',
    unknown_customer: 'Unknown Customer',
    current_balances: 'Current Balances',
    no_wallets: 'No wallets',
    page: 'Page',
    of: 'of',
    no_transactions_found_for_customer: 'No transactions found for this customer.',
    amount: 'Amount',
    export_pdf: 'Export PDF',
    exporting: 'Exporting…',
    // Transaction History
    client: 'Client',
    currency: 'Currency',
    balance: 'Balance',
    all: 'All',
    today: 'Today',
    last_7_days: 'Last 7 days',
    last_30_days: 'Last 30 days',
    this_month: 'This month',
    all_time: 'All time',
    date_from: 'Date from',
    date_to: 'Date to',
    search: 'Search',
    loading: 'Loading…',
    results: 'results',
    no_results: 'No results',
    close: 'Close',
    // Login
    sign_in_to_continue: 'Sign in to continue',
    username: 'Username',
    password: 'Password',
    sign_in: 'Sign in',
    demo_credentials: 'Demo credentials:',
    // Module picker
    choose_module: 'Choose a module',
    choose_module_desc: 'Select which area of the business you want to work on.',
    module_custody: 'Custody',
    module_custody_desc: 'Manage client money custody (USD, EUR, SRD): deposits, withdrawals, balances and transaction history.',
    module_gold: 'Gold',
    module_gold_desc: 'Track physical gold per client and per purity (teor). Deposits, withdrawals and history.',
    module_loans: 'Loans',
    module_loans_desc: 'Manage client loans, interest and repayment schedules.',
    coming_soon: 'Coming soon',
    back_to_modules: 'Back to modules',
    // Gold module
    gold_holdings: 'Gold Holdings',
    no_gold_lots: 'No gold for this client yet',
    make_gold_transaction: 'Make a Gold Transaction',
    purity: 'Purity (Teor)',
    purity_hint_deposit: 'A new purity creates a new lot. Same purity merges into the existing lot.',
    purity_hint_withdraw: 'Withdrawals are taken from a single lot. Purities are never mixed.',
    withdraw_from_lot: 'Withdraw from lot',
    select_lot_placeholder: 'Select a lot...',
    select_lot_first: 'Please select a lot to withdraw from.',
    insufficient_gold_in_lot: 'Not enough gold in this lot.',
    invalid_purity: 'Purity must be between 0 and 100.',
    lot: 'Lot',
    total: 'Total',
    unit: 'Unit',
    all_clients_gold_total: 'All Clients Gold',
  },
  'pt-BR': {
    brand_title: 'GL Compra de Ouro',
    customers: 'Clientes',
    search_placeholder: 'Pesquisar por nome, informações ou telefone',
    signed_in_as: 'Logado como',
    logout: 'Sair',
    current_balance: 'Saldo Atual',
    make_transaction: 'Fazer Transação',
    type: 'Tipo',
    deposit: 'Depósito',
    withdraw: 'Saque',
    amount: 'Valor',
    description: 'Descrição',
    description_placeholder: 'ex.: Depósito da venda de ouro',
    date: 'Data',
    add_transaction: 'Adicionar Transação',
    transaction_history: 'Histórico de Transações',
    no_transactions: 'Nenhuma transação ainda',
    no_customers: 'Nenhum cliente encontrado',
    select_customer: 'Selecione um cliente para ver detalhes',
    // Customers
    add_client: 'Adicionar Cliente',
    new_client: 'Novo Cliente',
    edit_client: 'Editar Cliente',
    name: 'Nome',
    email: 'E-mail',
    phone: 'Telefone',
    additional_info: 'Informações adicionais',
    cancel: 'Cancelar',
    create: 'Criar',
    save: 'Salvar',
    edit: 'Editar',
    delete: 'Excluir',
    delete_confirm: 'Excluir este cliente? Isso também removerá suas transações.',
    delete_tx_confirm: 'Excluir esta transação?',
    edit_transaction: 'Editar Transação',
    saving: 'Salvando…',
    invalid_amount: 'Valor inválido',
    all_clients_total: 'Saldo de todos os clientes',
    transaction_statement: 'Extrato de Transações',
    generated: 'Gerado',
    unknown_customer: 'Cliente Desconhecido',
    current_balances: 'Saldos Atuais',
    no_wallets: 'Sem carteiras',
    page: 'Página',
    of: 'de',
    no_transactions_found_for_customer: 'Nenhuma transação encontrada para este cliente.',
    amount: 'Valor',
    export_pdf: 'Exportar PDF',
    exporting: 'Exportando…',
    // Histórico de Transações
    client: 'Cliente',
    currency: 'Moeda',
    balance: 'Saldo',
    all: 'Todos',
    today: 'Hoje',
    last_7_days: 'Últimos 7 dias',
    last_30_days: 'Últimos 30 dias',
    this_month: 'Este mês',
    all_time: 'Todo período',
    date_from: 'Data de',
    date_to: 'Data até',
    search: 'Buscar',
    loading: 'Carregando…',
    results: 'resultados',
    no_results: 'Sem resultados',
    close: 'Fechar',
    // Login
    sign_in_to_continue: 'Faça login para continuar',
    username: 'Usuário',
    password: 'Senha',
    sign_in: 'Entrar',
    demo_credentials: 'Credenciais de demonstração:',
    // Module picker
    choose_module: 'Escolha um módulo',
    choose_module_desc: 'Selecione a área do negócio em que você quer trabalhar.',
    module_custody: 'Custódia',
    module_custody_desc: 'Gerencie a custódia de dinheiro dos clientes (USD, EUR, SRD): depósitos, saques, saldos e histórico.',
    module_gold: 'Ouro',
    module_gold_desc: 'Controle o ouro físico por cliente e por teor. Depósitos, saques e histórico.',
    module_loans: 'Empréstimos',
    module_loans_desc: 'Gerencie empréstimos a clientes, juros e parcelamentos.',
    coming_soon: 'Em breve',
    back_to_modules: 'Voltar aos módulos',
    // Módulo de Ouro
    gold_holdings: 'Ouro do Cliente',
    no_gold_lots: 'Nenhum ouro registrado para este cliente',
    make_gold_transaction: 'Fazer Transação de Ouro',
    purity: 'Teor',
    purity_hint_deposit: 'Um novo teor cria um novo lote. Mesmo teor adiciona ao lote existente.',
    purity_hint_withdraw: 'Saques são feitos de um único lote. Teores nunca são misturados.',
    withdraw_from_lot: 'Sacar do lote',
    select_lot_placeholder: 'Selecione um lote...',
    select_lot_first: 'Selecione um lote para sacar.',
    insufficient_gold_in_lot: 'Ouro insuficiente neste lote.',
    invalid_purity: 'O teor deve estar entre 0 e 100.',
    lot: 'Lote',
    total: 'Total',
    unit: 'Unidade',
    all_clients_gold_total: 'Ouro de Todos os Clientes',
  },
};

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState('en');

  useEffect(() => {
    const saved = localStorage.getItem(LOCALE_KEY);
    if (saved && messages[saved]) setLocale(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem(LOCALE_KEY, locale);
  }, [locale]);

  const t = (key) => {
    const dict = messages[locale] || messages.en;
    return dict[key] || key;
  };

  const value = useMemo(() => ({ locale, setLocale, t, messages }), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
}
